import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import {
  getShowAssetAllocationTab,
  INSURANCE_TYPES_WITH_BENEFICIARIES_TAB,
} from './UpsertInsuranceModal.meta';
import { useAccountPortfolioMutation } from '../../../../Profile/Accounts/api/useAccountPortfolioMutation';
import { useAccountBeneficiariesMutation } from '../../../../Profile/Accounts/api/useAccountBeneficiariesMutation';
import { useUpsertInsuranceMutation } from '../../api/useUpsertInsuranceMutation';
import { ACCOUNTS_QUERY_KEY } from '../../../../Profile/Accounts/AccountsConstants';
import {
  ACCOUNT_OPTIONS_QUERY_KEY,
  AccountType,
} from '../../../../Profile/Accounts/api/useAccountsOptionsQuery';
import { queryKeys } from '../../api/queryKeys';
import { InsuranceToUpsert } from '../../Insurance.meta';

interface Props {
  clientId: string;
  onClose?(): void;
}

export const useUpsertInsuranceModal = ({ clientId, onClose }: Props) => {
  const [isSaving, setIsSaving] = useState(false);
  const queryClient = useQueryClient();

  const { mutateAsync: saveInsurance, error: upsertInsuranceError } = useUpsertInsuranceMutation();

  const {
    mutateAsync: saveAccountPortfolio,
    error: saveAccountPortfolioError,
  } = useAccountPortfolioMutation();

  const {
    mutateAsync: saveBeneficiaries,
    error: saveBeneficiariesError,
  } = useAccountBeneficiariesMutation();

  const upsertError = upsertInsuranceError || saveAccountPortfolioError || saveBeneficiariesError;

  const handleSubmit = async ({ values, insurance, isClone }) => {
    try {
      setIsSaving(true);

      const { AccountId } = await saveInsurance({
        ...values,
        clientId,
        AccountId: isClone ? undefined : insurance.AccountId,
        AccountType: AccountType.Insurance,
        InsuranceType: insurance.InsuranceType,
      });

      const showAssetAllocationTab = getShowAssetAllocationTab({
        insurance: { ...insurance, ...values } as any,
      });

      if (showAssetAllocationTab && values.accountPortfolio) {
        await saveAccountPortfolio({
          AccountPortfolioByTaxStatuses: [
            {
              TaxStatus: null,
              HypotheticalReturn: values.accountPortfolio.hypotheticalReturn,
              AssetAllocation: values.accountPortfolio.assets,
            },
          ],
          AccountId,
        });
      }

      if (
        values.AccountBeneficiaries &&
        typeof insurance.InsuranceType === 'number' &&
        INSURANCE_TYPES_WITH_BENEFICIARIES_TAB.includes(insurance.InsuranceType)
      ) {
        await saveBeneficiaries({
          ...values.AccountBeneficiaries,
          AccountId,
        });
      }

      await Promise.all([
        queryClient.refetchQueries(queryKeys.insurance(clientId)),
        queryClient.refetchQueries([ACCOUNTS_QUERY_KEY, ACCOUNT_OPTIONS_QUERY_KEY]),
      ]);

      toast.success(
        insurance.AccountId
          ? isClone
            ? 'Insurance policy cloned successfully.'
            : 'Insurance policy saved successfully.'
          : 'Insurance policy added successfully.',
      );
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error(error);

      setIsSaving(false);
    }
  };
  return { data: { isSaving, upsertError }, actions: { handleSubmit } };
};
